import { useGetAccessChallengeQuery } from '@massilia/queries'
import { Button, Input, Label } from '@massilia/ui'
import { useState } from 'react'

interface PersonalDataAccessFormProps {
  description: string
  errorMessage: null | string
  isSubmitting: boolean
  onSubmit: (credentials: { answer: string; challenge: string; lastName: string }) => void
  submitLabel: string
  title: string
}

/**
 * Mur d'accès aux données personnelles : le nom de famille, et un petit calcul.
 *
 * Il sert les trois surfaces publiques — proposition, demande, confirmation de
 * réservation : mêmes gestes, mêmes règles, seuls les mots changent
 * (ACCES-PERSO-NOM).
 *
 * Le calcul (ACCES-PERSO-DEFI) écarte le script qui poste des noms au hasard :
 * il faut désormais demander une question au serveur et y répondre. Il ne coûte
 * qu'une addition à un humain, et il n'apparaît jamais sur le chemin des liens
 * de mail — un jeton valide ouvre la page sans passer par ici.
 *
 * Le message d'erreur du nom est le même pour toutes les causes d'échec —
 * c'est aussi ce que renvoie le serveur. Le formulaire ne doit pas être plus
 * bavard que lui.
 */
export function PersonalDataAccessForm({
  description,
  errorMessage,
  isSubmitting,
  onSubmit,
  submitLabel,
  title,
}: PersonalDataAccessFormProps) {
  const [lastName, setLastName] = useState('')
  const [answer, setAnswer] = useState('')

  const challengeQuery = useGetAccessChallengeQuery()
  const challenge = challengeQuery.data

  const canSubmit =
    lastName.trim().length > 0 && answer.trim().length > 0 && !!challenge && !isSubmitting

  return (
    <form
      className="mx-auto flex w-full max-w-lg flex-col gap-4 rounded-2xl border border-gray-light-200 p-4 md:p-6"
      onSubmit={(event) => {
        event.preventDefault()
        if (!challenge) return

        onSubmit({
          lastName: lastName.trim(),
          answer: answer.trim(),
          challenge: challenge.challenge,
        })

        // Une question ne se rejoue pas : la suivante est demandée dès l'envoi,
        // qu'il aboutisse ou non.
        setAnswer('')
        void challengeQuery.refetch()
      }}
    >
      <div className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-gray-light-900">{title}</h1>
        <p className="text-sm text-gray-light-600">{description}</p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="access-last-name">Nom de famille</Label>
        <Input
          autoComplete="family-name"
          id="access-last-name"
          onChange={(event) => setLastName(event.target.value)}
          placeholder="Nom de famille"
          value={lastName}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="access-answer">
          {challenge ? challenge.question : 'Vérification en cours…'}
        </Label>
        <Input
          autoComplete="off"
          disabled={!challenge}
          id="access-answer"
          inputMode="numeric"
          onChange={(event) => setAnswer(event.target.value)}
          placeholder="Votre réponse"
          value={answer}
        />
        <p className="text-xs text-gray-light-600">Une simple addition, pour écarter les robots.</p>
      </div>

      {errorMessage && (
        <p className="text-sm text-red-600" role="alert">
          {errorMessage}
        </p>
      )}

      <Button
        className="bg-brand-600 text-white hover:bg-brand-700"
        disabled={!canSubmit}
        type="submit"
      >
        {submitLabel}
      </Button>
    </form>
  )
}
